import {
    AuthContext,
    ISelectOption,
    TypeFilterBar,
    TypeFilterChip,
    ToastContext,
} from '@enter/core';
import {
    mdiAccountCashOutline,
    mdiCheckCircleOutline,
    mdiCogOutline,
    mdiEmailFastOutline,
    mdiProgressClock,
    mdiHub,
    mdiOpenInNew,
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DataTableCard, PageLayout, PageLayoutRow, PageLayoutTitle } from 'components';
import { DialogContext } from 'services';
import { IObservedRootStore, PatientCollectionsTab } from 'stores';
import { renderCollectionAmount } from './patient-collections-cells';
import { PatientCollectionsSearch } from './patient-collections-search';
import * as S from './patient-collections.styles';
import { PatientCollectionsNeeds } from './patient-collections-needs';
import { PatientCollectionsReady } from './patient-collections-ready';
import { PatientCollectionsOutstanding } from './patient-collections-outstanding';
import { PatientCollectionsPaid } from './patient-collections-paid';
import { ResolveInvoiceDialog } from './resolve-invoice-dialog';
import { ConfirmDialog } from '../settings/components/confirm-dialog';
import { Variables } from '@enter/core';
const TABS: { id: PatientCollectionsTab; label: string; countKind: 'needs' | 'paid' | 'neutral' }[] = [
    { id: 'needs', label: 'Needs Input (Drafts)', countKind: 'needs' },
    { id: 'ready', label: 'Ready to Send', countKind: 'neutral' },
    { id: 'outstanding', label: 'Outstanding', countKind: 'neutral' },
    { id: 'paid', label: 'Paid', countKind: 'paid' },
];

function patientName(first?: string, last?: string): string {
    return [first, last].filter(Boolean).join(' ') || '—';
}

export const PatientCollections: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        const navigate = useNavigate();
        const { tab, accountId } = useParams<{ tab: PatientCollectionsTab; accountId?: string }>();
        const store = rootStore.collectionInvoiceStore;

        const [bootstrapping, setBootstrapping] = useState(true);
        const [refreshKey, setRefreshKey] = useState(0);
        const [confirmingId, setConfirmingId] = useState<string | undefined>();
        const [searchValue, setSearchValue] = useState('');
        const [patientIdSearch, setPatientIdSearch] = useState('');

        const basePath = accountId ? `/${accountId}/patient-collections` : '/patient-collections';

        // Integrations first; summary follows selectedIntegrationId in its own effect
        // (same trigger the tables already remount on).
        const bootstrap = useCallback(async () => {
            try {
                await store.loadIntegrations();
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setBootstrapping(false);
            }
        }, [dialogContext, store]);

        const reloadForCurrentIntegration = useCallback(async () => {
            const integrationId = store.selectedIntegrationId;
            if (!integrationId) {
                return;
            }
            try {
                authContext.cancelGroup?.('patient-collections-summary');
                authContext.setGroup?.('patient-collections-summary');
                await store.loadSummary(integrationId, patientIdSearch);
                setRefreshKey(k => k + 1);
            } catch (err) {
                dialogContext.showError(err);
            }
        }, [authContext, dialogContext, store, patientIdSearch]);

        // Mount once — do not depend on context/callback identity (observer + unstable
        // AuthContext value was re-firing this and hammering collection_invoice_integration).
        useEffect(() => {
            document.title = 'Provider Dashboard - Patient Collections';
            void bootstrap();
        }, []);

        // Tables remount on selectedIntegrationId; summary must reload on the same id and the same
        // patient search. Do not cancelGroup here — cleanup abort (Strict Mode / /needs redirect)
        // left KPIs empty; the store guards stale responses instead.
        useEffect(() => {
            const integrationId = store.selectedIntegrationId;
            if (!integrationId) {
                return;
            }
            let cancelled = false;
            (async () => {
                try {
                    authContext.setGroup?.('patient-collections-summary');
                    await store.loadSummary(integrationId, patientIdSearch);
                } catch (err) {
                    if (!cancelled) {
                        dialogContext.showError(err);
                    }
                }
            })();
            return () => {
                cancelled = true;
            };
        }, [store.selectedIntegrationId, patientIdSearch]);

        const integrationOptions: ISelectOption[] = useMemo(
            () =>
                store.integrations.map(ig => ({
                    label: ig.name || ig.id || '—',
                    value: ig.id,
                })),
            [store.integrations],
        );

        const selectedIntegration = useMemo(
            () => integrationOptions.find(o => o.value === store.selectedIntegrationId) || null,
            [integrationOptions, store.selectedIntegrationId],
        );

        if (!tab || !TABS.some(t => t.id === tab)) {
            return <Navigate to={`${basePath}/needs`} replace />;
        }

        const summary = store.summary;
        const kpiPending = bootstrapping || store.summaryLoading;
        const draftCount = summary?.draft?.count;
        const waitingCount = summary?.waiting?.count;
        const sentCount = summary?.sent?.count;
        const paidCount = summary?.paid?.count;

        const kpiAmount = (detail?: unknown): string => {
            const formatted = renderCollectionAmount(detail);
            if (formatted !== '—') {
                return formatted;
            }
            return kpiPending ? '…' : '—';
        };

        const kpiCountLine = (count: number | undefined, loaded: string): string => {
            if (count == null) {
                return kpiPending ? 'Loading…' : '—';
            }
            return loaded;
        };

        const tabCount = (id: PatientCollectionsTab): number | undefined => {
            if (id === 'needs') return draftCount;
            if (id === 'ready') return waitingCount;
            if (id === 'outstanding') return sentCount;
            return paidCount;
        };

        const onIntegrationChange = (option: ISelectOption | ISelectOption[] | null) => {
            const value = Array.isArray(option) ? option[0]?.value : option?.value;
            if (typeof value !== 'string' || !value || value === store.selectedIntegrationId) {
                return;
            }
            store.selectIntegration(value);
            setRefreshKey(k => k + 1);
        };

        const openResolve = (draftId: string) => {
            const draft = store.findDraft(draftId);
            if (!draft) {
                toastContext.notify?.('Draft not found', 'right');
                return;
            }
            const handleClose = () => dialogContext.hideDialog();
            dialogContext.showDialog({
                props: {
                    onClose: handleClose,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Resolve Invoice',
                    expandable: false,
                    maxWidth: 'xl',
                    fullWidth: true,
                    sx: {
                        '& .MuiDialog-paper': {
                            width: '1080px',
                            maxWidth: 'calc(100vw - 48px)',
                            height: '92vh',
                            maxHeight: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                        '& .MuiDialogTitle-root': {
                            flex: '0 0 auto',
                        },
                        '& .MuiDialogContent-root': {
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden !important',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 0,
                        },
                    },
                    slotProps: {
                        paper: {
                            sx: {
                                width: '1080px',
                                maxWidth: 'calc(100vw - 48px)',
                                height: '92vh',
                                maxHeight: '92vh',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            },
                        },
                    },
                },
                Component: (
                    <ResolveInvoiceDialog
                        key={draft.collectionInvoiceDraftId || draft.claimId}
                        draft={draft}
                        onClose={handleClose}
                        onResolved={async opts => {
                            const currentId = draft.collectionInvoiceDraftId;
                            const nextId =
                                opts?.advance && currentId
                                    ? store.nextOpenDraftId(currentId)
                                    : undefined;
                            if (nextId) {
                                openResolve(nextId);
                            } else {
                                handleClose();
                            }
                            await reloadForCurrentIntegration();
                        }}
                    />
                ),
            });
        };

        const confirmBalance = async (draftId: string) => {
            if (confirmingId) {
                return;
            }
            const draft = store.findDraft(draftId);
            const name = patientName(draft?.patientFirstName, draft?.patientLastName);
            const handleCancel = (): void => {
                dialogContext.hideDialog();
            };
            const handleSubmit = async (): Promise<void> => {
                setConfirmingId(draftId);
                try {
                    await store.approveDraftInvoice(draftId, {});
                    dialogContext.hideDialog();
                    toastContext.notify?.('Balance confirmed — ready to bill', 'right');
                    await reloadForCurrentIntegration();
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setConfirmingId(undefined);
                }
            };
            dialogContext.showDialog({
                props: {
                    onClose: handleCancel,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle:
                        name !== '—' ? `Confirm balance for ${name}?` : 'Confirm this balance?',
                },
                Component: (
                    <ConfirmDialog
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        message="This queues the invoice for tonight’s statement batch."
                        yesMsg="Confirm Balance"
                        noMsg="Cancel"
                    />
                ),
            });
        };

        const openInvoiceStatement = (invoiceId: string) => {
            navigate(`${basePath}/invoice/${invoiceId}`, { state: { fromTab: tab } });
        };

        const titles: Record<PatientCollectionsTab, [string, string]> = {
            needs: [
                'Invoices awaiting your input',
                'Confirm each patient’s out-of-pocket cost, then queue for billing',
            ],
            ready: [
                'Ready to send',
                'Queued for tonight’s automatic statement batch · no action needed',
            ],
            outstanding: ['Outstanding invoices', 'Billed to patients · awaiting payment'],
            paid: ['Paid invoices', 'Paid in full by the patient · no balance due'],
        };
        const [tableTitle, tableSub] = titles[tab];

        const notConfigured = !bootstrapping && store.integrations.length === 0;

        if (notConfigured) {
            return (
                <PageLayout>
                    <S.NotConfiguredBanner>
                        <S.NotConfiguredTitle>
                            Not configured - contact your Customer Success Associate
                        </S.NotConfiguredTitle>
                    </S.NotConfiguredBanner>
                </PageLayout>
            );
        }

        return (
            <PageLayout>
                <PageLayoutRow>
                    <S.TitleRow>
                        <PageLayoutTitle>Patient Collections</PageLayoutTitle>
                        <S.HeaderExtras>
                            <S.IntegrationWrap>
                                <S.IntegrationLabel>Integration</S.IntegrationLabel>
                                {store.integrations.length > 0 ? (
                                    <S.IntegrationSelectShell>
                                        <S.IntegrationSelectIcon>
                                            <Icon path={mdiHub} size={0.7} />
                                        </S.IntegrationSelectIcon>
                                        <S.IntegrationSelect
                                            value={selectedIntegration}
                                            onChange={onIntegrationChange as any}
                                            options={integrationOptions}
                                            isDisabled={bootstrapping || store.summaryLoading}
                                            isSearchable={false}
                                            placeholder="Select integration"
                                        />
                                    </S.IntegrationSelectShell>
                                ) : (
                                    <S.IntegrationStatic>
                                        <S.IntegrationSelectIcon>
                                            <Icon path={mdiHub} size={0.7} />
                                        </S.IntegrationSelectIcon>
                                        {bootstrapping ? '…' : '—'}
                                    </S.IntegrationStatic>
                                )}
                            </S.IntegrationWrap>
                        </S.HeaderExtras>
                        <PatientCollectionsSearch
                            value={searchValue}
                            onChange={setSearchValue}
                            onCommit={setPatientIdSearch}
                        />
                    </S.TitleRow>
                </PageLayoutRow>

                <S.KpiGrid>
                    <S.KpiCard>
                        <S.KpiLabel>
                            <Icon path={mdiProgressClock} size={0.7} color={Variables.ThemeLight.AttentionConstant6} />
                            Awaiting Input
                        </S.KpiLabel>
                        <S.KpiValue>{kpiAmount(summary?.draft)}</S.KpiValue>
                        <S.KpiSubWarn>
                            {kpiCountLine(
                                draftCount,
                                `${draftCount} need your input`,
                            )}
                        </S.KpiSubWarn>
                    </S.KpiCard>
                    <S.KpiCard>
                        <S.KpiLabel>
                            <Icon path={mdiEmailFastOutline} size={0.7} color={Variables.ThemeLight.PrimaryConstant6} />
                            Ready to Send
                        </S.KpiLabel>
                        <S.KpiValue>{kpiAmount(summary?.waiting)}</S.KpiValue>
                        <S.KpiSub>
                            {kpiCountLine(waitingCount, `${waitingCount} queued`)}
                        </S.KpiSub>
                    </S.KpiCard>
                    <S.KpiCard>
                        <S.KpiLabel>
                            <Icon path={mdiAccountCashOutline} size={0.7} />
                            Total Outstanding
                        </S.KpiLabel>
                        <S.KpiValue>{kpiAmount(summary?.sent)}</S.KpiValue>
                        <S.KpiSub>
                            {kpiCountLine(sentCount, `${sentCount} invoices billed`)}
                        </S.KpiSub>
                    </S.KpiCard>
                    <S.KpiCard>
                        <S.KpiLabel>
                            <Icon path={mdiCheckCircleOutline} size={0.7} color={Variables.ThemeLight.SuccessConstant6} />
                            Paid
                        </S.KpiLabel>
                        <S.KpiValue>{kpiAmount(summary?.paid)}</S.KpiValue>
                        <S.KpiSub>
                            {kpiCountLine(paidCount, `${paidCount} invoices paid in full`)}
                        </S.KpiSub>
                    </S.KpiCard>
                </S.KpiGrid>
                <S.TabsContainer>
                    <TypeFilterBar>
                        {TABS.map(t => {
                            const isActive = tab === t.id;
                            const count = tabCount(t.id);
                            return (
                                <TypeFilterChip
                                    key={t.id}
                                    active={isActive}
                                    disabled={bootstrapping}
                                    onClick={() => navigate(`${basePath}/${t.id}`)}>
                                    {t.label}
                                    {typeof count === 'number' && (
                                        <S.TabCountPill $kind={t.countKind}>{count}</S.TabCountPill>
                                    )}
                                </TypeFilterChip>
                            );
                        })}
                    </TypeFilterBar>
                    <S.TabToolbar>
                        <S.WorkerConfigButton
                            variant="outlined"
                            onClick={() =>
                                navigate(accountId ? `/${accountId}/configuration` : '/configuration')
                            }>
                            <Icon path={mdiCogOutline} size={0.7} />
                            <span>Worker Configuration</span>
                            <Icon path={mdiOpenInNew} size={0.55} />
                        </S.WorkerConfigButton>
                    </S.TabToolbar>
                </S.TabsContainer>
                <PageLayoutRow>
                    <S.TableCardWrap>
                    <DataTableCard size="large">

                        <S.TableHeader>
                            <S.TableTitle>{tableTitle}</S.TableTitle>
                            <S.TableSub>{tableSub}</S.TableSub>
                            <S.TableHeaderActions>
                                {tab === 'ready' && (
                                    <S.NightlyHint>Sends automatically tonight</S.NightlyHint>
                                )}
                            </S.TableHeaderActions>
                        </S.TableHeader>

                        {tab === 'needs' && !bootstrapping && store.selectedIntegrationId && (
                            <PatientCollectionsNeeds
                                key={`needs-${refreshKey}-${store.selectedIntegrationId}`}
                                onResolve={openResolve}
                                onConfirmBalance={confirmBalance}
                                confirmingId={confirmingId}
                                patientName={patientName}
                                patientIdSearch={patientIdSearch}
                            />
                        )}
                        {tab === 'ready' && !bootstrapping && store.selectedIntegrationId && (
                            <PatientCollectionsReady
                                key={`ready-${refreshKey}-${store.selectedIntegrationId}`}
                                onOpen={openInvoiceStatement}
                                patientName={patientName}
                                patientIdSearch={patientIdSearch}
                            />
                        )}
                        {tab === 'outstanding' && !bootstrapping && store.selectedIntegrationId && (
                            <PatientCollectionsOutstanding
                                key={`out-${refreshKey}-${store.selectedIntegrationId}`}
                                onOpen={openInvoiceStatement}
                                patientName={patientName}
                                patientIdSearch={patientIdSearch}
                            />
                        )}
                        {tab === 'paid' && !bootstrapping && store.selectedIntegrationId && (
                            <PatientCollectionsPaid
                                key={`paid-${refreshKey}-${store.selectedIntegrationId}`}
                                onOpen={openInvoiceStatement}
                                patientName={patientName}
                                patientIdSearch={patientIdSearch}
                            />
                        )}
                    </DataTableCard>
                    </S.TableCardWrap>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);

export { patientName };

function fmtAmt(value: unknown): string {
    return renderCollectionAmount(value);
}
export { fmtAmt };
